import styled from "styled-components"
import Header from "../../Common/Header"
import Footer from "../../Common/Footer"
import { futuristicListMarker } from "@components/base/listMarkers"

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(2.5rem, 6vw, 4rem);
  background: #f5f8fb;
`

const Hero = styled.section`
  position: relative;
  min-height: min(720px, 76vh);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(5rem, 10vw, 7rem) 1.5rem;
  color: #ffffff;
  background: url("/img/interieur-maison-unite.jpg") center/cover no-repeat;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(11, 40, 71, 0.92) 10%, rgba(19, 77, 121, 0.72) 55%, rgba(26, 115, 173, 0.62) 100%);
    z-index: 0;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: min(1080px, 92vw);
  display: grid;
  gap: 1.6rem;
  text-align: center;
  backdrop-filter: blur(6px);
`

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.3rem;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.18);
  letter-spacing: 0.12em;
  font-size: 0.78rem;
  text-transform: uppercase;
`

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.6rem, 6vw, 4rem);
  line-height: 1.1;
  color: #fff;
`

const HeroSubtitle = styled.p`
  margin: 0 auto;
  max-width: 760px;
  font-size: clamp(1.05rem, 2vw, 1.35rem);
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.8;
`

const Article = styled.article`
  width: min(960px, 92vw);
  margin: 0 auto;
  background: #ffffff;
  border-top: 4px solid #337abd;
  border-radius: 2px;
  padding: clamp(2rem, 5vw, 3rem);
  box-shadow: 0 24px 60px rgba(13, 65, 114, 0.12);
  display: grid;
  gap: 1.6rem;
  color: #1b2e41;
`

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  color: #103c68;
`

const Paragraph = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.8;
`

const BulletList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.8rem;
  font-size: 1.05rem;
  line-height: 1.7;

  li {
    ${futuristicListMarker};
    color: #1b2e41;
  }
`

const ChecklistCard = styled.div`
  background: rgba(27, 117, 188, 0.08);
  border-radius: 2px;
  padding: 1.5rem;
  display: grid;
  gap: 0.6rem;
  border: 1px solid rgba(27, 117, 188, 0.18);
`

const PreparerPlexHiverNeuville = () => {
  return (
    <PageWrapper>
      <Header />
      <main id="contenu-principal">
        <Hero>
          <HeroContent>
            <HeroBadge>Entretien saisonnier</HeroBadge>
            <HeroTitle>Préparer son plex pour l'hiver à Neuville</HeroTitle>
            <HeroSubtitle>
              Une liste de vérification en quatre étapes pour préparer le bâtiment, les équipements et les communications avant le froid.
            </HeroSubtitle>
          </HeroContent>
        </Hero>

        <Article>
          <Paragraph>
            Le gel, la neige et les écarts de température sollicitent rapidement un immeuble. Une préparation structurée aide à repérer les besoins,
            coordonner les fournisseurs et transmettre des consignes claires aux occupants.
          </Paragraph>

          <SectionTitle>Semaine 1 – Inspection extérieure</SectionTitle>
          <BulletList>
            <li>Inspecter toitures, gouttières et descentes pluviales; enlever feuilles et débris.</li>
            <li>Vérifier le calfeutrage des portes et fenêtres, remplacer les coupe-froid au besoin.</li>
            <li>Planifier le déneigement (contrats signés, responsables identifiés, numéros d'urgence disponibles).</li>
          </BulletList>

          <SectionTitle>Semaine 2 – Systèmes mécaniques</SectionTitle>
          <BulletList>
            <li>Entretien préventif des fournaises ou thermopompes (filtres, échangeurs, combustion).</li>
            <li>Purge des radiateurs et vérification des thermostats dans les aires communes et logements vacants.</li>
            <li>Test des génératrices ou systèmes d'alimentation d'urgence si présents.</li>
          </BulletList>

          <SectionTitle>Semaine 3 – Préparation des logements</SectionTitle>
          <Paragraph>
            Communiquez avec vos locataires : un mémo clair avec contacts d'urgence, règles de ventilation et rappels de sécurité (balcons,
            stationnement). Profitez-en pour vérifier extincteurs, détecteurs et trappes d'accès.
          </Paragraph>
          <ChecklistCard>
            <strong>À rappeler aux locataires :</strong>
            <BulletList>
              <li>Maintenir une température suffisante pour éviter le gel et respecter les conditions du bail.</li>
              <li>Signaler immédiatement toute fuite ou bruit suspect dans la plomberie.</li>
              <li>Libérer les issues de secours et balcons de toute accumulation de neige.</li>
            </BulletList>
          </ChecklistCard>

          <SectionTitle>Semaine 4 – Derniers ajustements</SectionTitle>
          <Paragraph>
            Passez en revue votre inventaire de sel, abrasifs et balises. Confirmez les fournisseurs pour les urgences (plombier, électricien,
            vitrier). Documentez le tout dans un dossier partagé avec votre gestionnaire ou vos partenaires.
          </Paragraph>

          <Paragraph>
            Conservez les confirmations de fournisseurs, les photos et les rapports d’entretien dans un même dossier. En cas de bris, l’information
            sera plus facile à transmettre à la bonne personne.
          </Paragraph>
        </Article>
      </main>
      <Footer />
    </PageWrapper>
  )
}

export default PreparerPlexHiverNeuville
